import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { parse, isSameDay, addHours, format } from 'date-fns';
import classnames from 'classnames';

import CustomRadioButton from '../../../CustomRadioButton/CustomRadioButton';
import DatePickerInputMobile from '../../../DatePickerInputMobile';
import DurationInput from 'components/DurationInput';
import TimeInput from 'components/TimeInput';
import { isIOS } from '../../../Option';
import IOSSwitch from '../../../IOSSwitch';
import { styles } from './styles';
import kioskPageActions from 'store/kiosk/kioskPageActions';
import { validateEndAfterStart } from '../../helpers/validate';
import { getNextDay } from '../../helpers/getNextDay';
import {
  formatData,
  getDefaultFormValues,
  getWeekends,
  reasons,
  relations,
} from './helpers/helpers';
import ConfirmDialog from '../../../ConfirmDialog';
import { ERROR_MESSAGES } from '../../../../helpers/error-messages.constant';
import { createOptions } from 'helpers/createOptions';
import $api from 'http/index';
import { getKioskOptions } from 'helpers/getOptions';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { useTimeSettingsFormat } from '../../../../common/useTimeSettingsFormat';
import { DAY_PART, convertTimeObject, convertTo24HourFormat } from 'common/dateFormatConfig';

const useStyles = makeStyles(styles);

const DURATION = ['durationHours', 'durationMinutes'];

const timeParts = (prefix, is12Format) =>
  ['Hours', 'Minutes', ...(is12Format ? ['AmPm'] : [])].map((part) => prefix + part);

const AddTimeOff = ({ user, pto, shiftTime }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { dateFormat, dateFormatWithTime } = useDateSettingsFormat();
  const getTimeFormatHook = useTimeSettingsFormat();
  const is12Format = getTimeFormatHook().is12Format;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogShown, setConfirmDialogShown] = useState(null);

  const userWeekends = useMemo(() => getWeekends(user), [user]);
  const dataReady = useMemo(() => Boolean(shiftTime), [shiftTime]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
    reset,
    getValues,
    setError,
    clearErrors,
    trigger,
    register,
    unregister,
  } = useForm({
    mode: 'onChange',
    defaultValues: dataReady && getDefaultFormValues({ pto, shiftTime, dateFormat, is12Format }),
  });
  const values = watch([
    'startDate',
    'endDate',
    'type',
    'reason',
    'startHours',
    'endHours',
    'startAmPm',
    'endAmPm',
    'relationship',
    'isPTO',
    'startMinutes',
    'endMinutes',
    'durationHours',
    'durationMinutes'
  ]);
  const isFull = values.type === 'full';
  const duration = { hours: values.durationHours, minutes: values.durationMinutes };

  useEffect(() => {
    const names = timeParts('start', is12Format);
    names.forEach((name) => register({ name }));
    return () => unregister(names);
  }, [is12Format, register, unregister]);

  useEffect(() => {
    const names = isFull ? timeParts('end', is12Format) : DURATION;
    names.forEach((name) => register({ name }));
    return () => unregister(names);
  }, [isFull, is12Format, register, unregister]);

  const isBereavement = useMemo(() => values.reason === reasons[2], [values.reason]);

  const isOther = useMemo(() => {
    return isBereavement && values.relationship === relations[relations.length - 1];
  }, [isBereavement, values.relationship]);

  const isNotSubtractedAvailablePTO = useMemo(() => {
    return isBereavement && (!isOther || !values.isPTO);
  }, [isBereavement, isOther, values.isPTO]);

  const isSubtractedErrorShown = useMemo(() => {
    return values.relationship && isBereavement && (!isOther || values.isPTO);
  }, [values.relationship, isBereavement, isOther, values.isPTO]);

  useEffect(() => {
    isBereavement ? setValue('relationship', '') : trigger('notes');
    setValue('notes', '');
  }, [values.reason === reasons[2]]);

  useEffect(() => {
    if (isBereavement && values.relationship !== relations[relations.length - 1])
      setValue('isPTO', true);
  }, [values.relationship]);

  useEffect(() => {
    setValue('isPTO', !!(pto && pto.available));
  }, [pto]);

  const closeConfirmDialog = () => setConfirmDialogShown(null);
  const handleConfirmSubmit = () => {
    const { relationship, ...values } = getValues();

    let resultData;
    if (isBereavement) {
      resultData = { relationship, ...values };
      resultData.relativeName = resultData.notes;
      delete resultData.notes;
    } else {
      resultData = values;
    }
    closeConfirmDialog();
    onSubmit(resultData);
  };
  const onSubmit = (valuesData) => {
    setIsSubmitting(true);

    const apiCall = async () => {
      try {
        const { relationship } = getValues();

        let values;
        if (isBereavement) {
          values = { relationship, ...valuesData };
          values.relativeName = values.notes;
          delete values.notes;
        } else {
          values = valuesData;
        }

        const data = {
          ...formatData({ values, user }, dateFormatWithTime, getTimeFormatHook),
          isForcePTO: !!confirmDialogShown,
        };
        data.startTime = {
          hour: convertTo24HourFormat(data.startTime.hour, data.startTime.amPm),
          minute: data.startTime.minute,
        };
        if (data.type === 'partial') {
          const start24Hours = +data.startTime.hour;
          const hour = (start24Hours + values.durationHours + Math.floor((+data.startTime.minute + values.durationMinutes) / 60)) % 24;
          const minute = (+data.startTime.minute + values.durationMinutes) % 60;
          data.endTime = {
            hour: `${hour}`.padStart(2, '0'),
            minute: `${minute}`.padStart(2, '0'),
          }
        } else {
          data.endTime = {
            hour: convertTo24HourFormat(data.endTime.hour, data.endTime.amPm),
            minute: data.endTime.minute,
          };
        }

        if (data.type === 'full') {
          delete data.hours;
          delete data.durationHours;
          delete data.durationMinutes;
        } else {
          data.hours = values.durationHours + (values.durationMinutes / 60)
        }

        const response = await $api.post(
          `${process.env.REACT_APP_BASE_URL}/kiosk/timeoffs
        `,
          data,
          getKioskOptions()
        );

        dispatch(kioskPageActions.setPto(response.data.summaryPTOHours));
        dispatch(
          kioskPageActions.setAlert({
            type: 'success',
            text: 'Request has been successfully sent',
          })
        );
        reset();
      } catch (e) {
        dispatch(kioskPageActions.setError(e?.response?.data?.message || e.message));
        if (e?.response?.data?.type === 'not-enough-pto-hours')
          return setConfirmDialogShown(e?.response?.data?.message || e.message);
        dispatch(
          kioskPageActions.setAlert({
            type: 'error',
            text: e?.response?.data?.message || e.message,
          })
        );
      }
    };
    return apiCall().then(() => setIsSubmitting(false));
  };

  const addObjectHours = (timeObject, addHours = 4, is12HourFormat = true) => {
    let { hour, minute, amPm } = timeObject;
    let hourInt = parseInt(hour, 10);
    let newHour;

    if (is12HourFormat) {
      // 12-hour format logic
      newHour = hourInt + addHours;
      if (newHour > 12) {
        newHour = newHour % 12 || 12; // Handle midnight and noon
        amPm = hourInt + addHours >= 12 ? (amPm === DAY_PART[0] ? DAY_PART[1] : DAY_PART[0]) : amPm;
      }
      if (newHour === 0) newHour = 12; // Adjust for midnight in 12-hour format
    } else {
      // 24-hour format logic
      newHour = hourInt + addHours;
      if (newHour >= 24) newHour %= 24; // Adjusts hour for the next day(s)
      amPm = undefined; // amPm is irrelevant in 24-hour format
    }

    const newTimeObject = {
      hour: newHour.toString().padStart(2, '0'),
      minute,
      amPm: is12HourFormat ? amPm : undefined,
    };
    return newTimeObject;
  };

  const time = (prefix) => ({
    hour: values[`${prefix}Hours`],
    minute: values[`${prefix}Minutes`],
    amPm: values[`${prefix}AmPm`],
  });

  const setTime = (prefix, { hour, minute, amPm }) => {
    setValue(`${prefix}Hours`, hour);
    setValue(`${prefix}Minutes`, minute);
    setValue(`${prefix}AmPm`, amPm);
  };

  const validateTimes = () =>
    validateEndAfterStart(getValues(), setError, clearErrors, getTimeFormatHook);

  const setPartialEndDate = ({ hour, minute, amPm }, { hours, minutes }) => {
    const start24Hours = is12Format ? convertTo24HourFormat(hour, amPm) : hour;
    const endHours = +start24Hours + hours + Math.floor((+minute + minutes) / 60);
    setValue(
      'endDate',
      endHours >= 24
        ? getNextDay(parse(values.startDate, dateFormat, new Date()), dateFormat)
        : values.startDate
    );
  };

  const changeStart = (start) => {
    setTime('start', start);
    if (isFull) {
      setTime('end', start);
      validateTimes();
    } else {
      setPartialEndDate(start, duration);
    }
  };

  const changeEnd = (end) => {
    setTime('end', end);
    validateTimes();
  };

  const changeDuration = (next) => {
    setValue('durationHours', next.hours);
    setValue('durationMinutes', next.minutes);
    setPartialEndDate(time('start'), next);
    if (!next.hours && !next.minutes) {
      setError('hours', { type: 'error', message: 'Timeoff duration should be longer than 0' });
    } else {
      clearErrors('hours');
    }
  };

  return (
    <Box display="flex" flexDirection="column" className={classes.addTimeOffWrapper}>
      <Typography variant="h2" className={classes.heading}>
        Add Time Off
      </Typography>
      {dataReady && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor="type">
            <Typography variant="body1" color="textSecondary">
              Select Type:
            </Typography>
            <Controller
              name="type"
              control={control}
              render={({ onChange, value }) => (
                <RadioGroup
                  id="type"
                  value={value}
                  onChange={(e) => {
                    const { value } = e.target;
                    const {
                      hour,
                      minute: startMinutes,
                      amPm: startAmPm,
                    } = convertTimeObject(shiftTime, is12Format);

                    const startHours = +hour;
                    const partialDuration = (values.durationHours || 4) + ((values.durationMinutes || 0) / 60)
                    const partialTime = addObjectHours(
                      shiftTime,
                      partialDuration,
                      getTimeFormatHook().is12Format
                    );

                    const startHoursObj = [values.startDate, startHours, startMinutes, startAmPm];
                    const parsedStartDate = parse(
                      getTimeFormatHook(startHoursObj).formattedTimeDate,
                      getTimeFormatHook(startHoursObj).formatForDateTimePars,
                      new Date()
                    );

                    const isPartialSameDay = isSameDay(
                      parse(values.startDate, dateFormat, new Date()),
                      addHours(parsedStartDate, partialDuration)
                    );
                    const partialDay = isPartialSameDay
                      ? values.startDate
                      : format(addHours(parsedStartDate, partialDuration), dateFormat);

                    onChange(value);
                    setValue(
                      'endDate',
                      value === 'full'
                        ? getNextDay(parse(values.startDate, dateFormat, new Date()), dateFormat)
                        : partialDay
                    );

                    setValue('startHours', startHours);
                    setValue('startMinutes', startMinutes);
                    setValue('startAmPm', startAmPm);
                    setValue('endHours', value === 'full' ? startHours : partialTime.hour || 12);
                    setValue('endMinutes', value === 'full' ? startMinutes : partialTime.minute || 0);
                    setValue(
                      'endAmPm',
                      value === 'full' ? DAY_PART[0] : partialTime.amPm || DAY_PART[0]
                    );

                    if (value === 'partial') {
                      setValue('durationHours', values.durationHours || Math.floor(values.hours) || 4);
                      setValue('durationMinutes', values.durationMinutes || ((values.hours * 60) % 60) || 4);
                    }
                    clearErrors();
                  }}
                  className={classes.radioGroup}
                  row
                >
                  <FormControlLabel
                    value="full"
                    control={<CustomRadioButton />}
                    label="Full Day(s)"
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="partial"
                    control={<CustomRadioButton />}
                    label="Partial Day"
                    labelPlacement="end"
                  />
                </RadioGroup>
              )}
            />
          </label>
          <Typography variant="h4" color="textSecondary" className={classes.text}>
            Please select the time when you will be unavailable
          </Typography>
          <div className={classes.formInputWrapper}>
            <Box className={classes.startFormBig}>
              <Box className={classes.startForm}>
                <label htmlFor="startDate">
                  <Typography variant="body1" color="textSecondary">
                    Start Date:
                  </Typography>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ onChange, value }) => (
                      <DatePickerInputMobile
                        selectedDay={value}
                        setFieldValue={(_, value) => {
                          const { startDate, startHours, startMinutes, startAmPm } = values;
                          const shiftTime = {
                            hour: startHours,
                            minute: startMinutes,
                            amPm: startAmPm,
                          };
                          const partialDuration = values.durationHours + (values.durationMinutes / 60);
                          const partialTime = addObjectHours(
                            shiftTime,
                            partialDuration,
                            getTimeFormatHook().is12Format
                          );
                          // const parsedStartDate = parse(
                          //   `${value} ${startHours}:${startMinutes} ${startAmPm}`,
                          //   dateFormatWithTime,
                          //   new Date()
                          // );

                          onChange(value);

                          if (value.type === 'full') {
                            setValue('endDate', getNextDay(parse(value, dateFormat, new Date()), dateFormat));
                            setValue('endHours', startHours);
                            setValue('endMinutes', startMinutes);
                            setValue('endAmPm', partialTime.amPm);
                          } else {
                            const start24Hours = getTimeFormatHook().is12Format
                              ? ((startHours % 12) + (/pm/i.test(startAmPm) ? 12 : 0))
                              : +startHours;
                            
                            if (((partialDuration * 60) + (start24Hours * 60) + (+startMinutes)) > 24 * 60) {
                              setValue('endDate', getNextDay(parse(value, dateFormat, new Date()), dateFormat));
                            } else {
                              setValue('endDate', value);
                            }
                          }
                        }}
                        field="startDate"
                        userWeekends={userWeekends}
                      />
                    )}
                  />
                </label>
                <label>
                  <Typography variant="body1" color="textSecondary">
                    Start Time:
                  </Typography>
                  <TimeInput
                    value={time('start')}
                    onChange={changeStart}
                    error={Boolean(errors.startHours)}
                    className={classes.field}
                  />
                </label>
              </Box>
              <Box className={classes.startForm}>
                <label htmlFor="endDate">
                  <Typography variant="body1" color="textSecondary">
                    End Date:
                  </Typography>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ onChange, value }) => (
                      <DatePickerInputMobile
                        selectedDay={value}
                        setFieldValue={(_, value) => onChange(value)}
                        field="endDate"
                        minDate={getNextDay(
                          parse(values.startDate, dateFormat, new Date()),
                          dateFormat
                        )}
                        userWeekends={userWeekends}
                        disabled={values.type === 'partial'}
                      />
                    )}
                  />
                </label>
                {isFull ? (
                  <label>
                    <Typography variant="body1" color="textSecondary">
                      End Time:
                    </Typography>
                    <TimeInput
                      value={time('end')}
                      onChange={changeEnd}
                      error={Boolean(errors.startHours)}
                      className={classes.field}
                    />
                    {errors.startHours ? (
                      <div className={classes.error} style={{ position: 'absolute' }}>
                        {errors.startHours.message}
                      </div>
                    ) : null}
                  </label>
                ) : (
                  <label>
                    <Typography variant="body1" color="textSecondary">
                      PTO Time:
                    </Typography>
                    <DurationInput
                      title="PTO time"
                      value={duration}
                      onChange={changeDuration}
                      maxHours={7}
                      minuteStep={15}
                      error={Boolean(errors.hours)}
                      className={classes.field}
                    />
                    {errors.hours ? (
                      <div className={classes.error} style={{ position: 'absolute' }}>
                        {errors.hours.message}
                      </div>
                    ) : null}
                  </label>
                )}
              </Box>
            </Box>
            <Box className={classes.startFormBig}>
              <Box className={classes.startForm}>
                <label htmlFor="isPTO" className={classes.pto}>
                  <Typography variant="body1" color="textSecondary">
                    PTO (paid time off):
                  </Typography>
                  <Controller
                    name="isPTO"
                    control={control}
                    render={({ value, onChange }) => (
                      <IOSSwitch
                        name="isPTO"
                        checked={value}
                        onChange={(_, value) => onChange(value)}
                        value="isPTO"
                      />
                    )}
                  />
                </label>
                <label htmlFor="reason">
                  <Typography variant="body1" color="textSecondary">
                    Reason:
                  </Typography>
                  <Controller
                    name="reason"
                    control={control}
                    as={
                      <TextField
                        select
                        id="reason"
                        name="reason"
                        variant="outlined"
                        fullWidth
                        className={classnames(classes.select, classes.selectMob)}
                        SelectProps={{
                          native: isIOS,
                        }}
                      >
                        {reasons.map(createOptions)}
                      </TextField>
                    }
                  />
                  {isSubtractedErrorShown && (
                    <div
                      className={classnames(
                        classes.error,
                        classes.commonSubtractedError,
                        isNotSubtractedAvailablePTO
                          ? classes.isNotSubtractedAvailablePTO
                          : classes.willSubtractedAvailablePTO
                      )}
                    >
                      {isNotSubtractedAvailablePTO
                        ? ERROR_MESSAGES.isNotSubtractedAvailablePTO
                        : ERROR_MESSAGES.willSubtractedAvailablePTO}
                    </div>
                  )}
                </label>
              </Box>
              <Box className={classes.startForm}>
                {isBereavement && (
                  <label htmlFor="relationship">
                    <Typography variant="body1" color="textSecondary">
                      Relationship:
                    </Typography>
                    <Controller
                      name="relationship"
                      control={control}
                      rules={{
                        required: "This field can't be blank",
                      }}
                      as={
                        <TextField
                          select
                          defaultValue=""
                          id="relationship"
                          name="relationship"
                          variant="outlined"
                          fullWidth
                          error={Boolean(errors.relationship)}
                          className={classnames(classes.select, classes.relationshipBlock)}
                          SelectProps={{
                            native: isIOS,
                          }}
                        >
                          {relations.map(createOptions)}
                        </TextField>
                      }
                    />
                    {errors.relationship ? (
                      <div className={classnames(classes.error, classes.errorMob)}>
                        {errors.relationship.message}
                      </div>
                    ) : null}
                  </label>
                )}
                <label htmlFor="notes" style={{ width: isBereavement ? 'unset' : '100%' }}>
                  <Typography variant="body1" color="textSecondary">
                    {isBereavement ? 'Name' : 'Notes'}:
                  </Typography>
                  <Controller
                    name="notes"
                    control={control}
                    rules={{
                      required: isBereavement && "This field can't be blank",
                      validate: (value) =>
                        value.trim().length <= 50 ||
                        'The field cannot contain more than 50 symbols. Please, check.',
                    }}
                    as={
                      <TextField
                        id="notes"
                        name="notes"
                        maxRows={2}
                        error={Boolean(errors.notes)}
                        className={classes.textArea}
                        variant="outlined"
                        multiline={!isBereavement}
                      />
                    }
                  />
                  {errors.notes ? (
                    <div className={classes.error}>{errors.notes.message}</div>
                  ) : null}
                </label>
              </Box>
            </Box>
            <Box>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                className={classes.submit}
                disabled={isSubmitting || !!Object.keys(errors).length}
                disableTouchRipple
              >
                Request Timeoff
              </Button>
            </Box>
          </div>
        </form>
      )}
      {!!confirmDialogShown && (
        <ConfirmDialog
          onClose={closeConfirmDialog}
          text={
            <span>
              {`${confirmDialogShown}`.split('. ').map((line) => (
                <>
                  <span key={line}>{line}</span> <br />
                </>
              ))}
            </span>
          }
          isOpen
          onSubmit={handleConfirmSubmit}
        />
      )}
    </Box>
  );
};

export default AddTimeOff;
